import httpClient from './httpClient';
import { API_ENDPOINTS } from './endpoints.ts';
import { KycRequest, PaginatedResponse, KycRequestUpdateRequest } from '@/models/kyc.ts';

export const getKycRequests = async () => {
    const response = await httpClient.get(API_ENDPOINTS.TRADER.KYC.LIST);
    return response.data;
};

export const submitKycRequest = async (formData: FormData) => {
    const response = await httpClient.post(API_ENDPOINTS.TRADER.KYC.CREATE, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const getAdminKycRequests = async (
    customer_id?: string
): Promise<PaginatedResponse<KycRequest>> => {
    const params = customer_id ? { customer_id } : undefined;
    const response = await httpClient.get<PaginatedResponse<KycRequest>>(API_ENDPOINTS.CRM.KYC, {
        params,
    });
    return response.data;
};

export const getKycRequestDetails = async (id: string): Promise<KycRequest> => {
    const response = await httpClient.get<KycRequest>(`${API_ENDPOINTS.CRM.KYC}/${id}`);
    return response.data;
};

export const updateKycRequestStatus = async (
    id: string,
    data: KycRequestUpdateRequest
): Promise<KycRequest> => {
    const response = await httpClient.put<KycRequest>(`${API_ENDPOINTS.CRM.KYC}/${id}`, data);
    return response.data;
};

// Legacy function for backward compatibility
export const updateKycRequestStatusLegacy = async (
    id: string,
    status: string,
    rejection_reason?: string
): Promise<KycRequest> => {
    const response = await httpClient.put<KycRequest>(`${API_ENDPOINTS.CRM.KYC}/${id}`, {
        status,
        rejection_reason,
    });
    return response.data;
};

export const createKycRequest = async (formData: FormData): Promise<KycRequest> => {
    const response = await httpClient.post<KycRequest>(API_ENDPOINTS.CRM.KYC, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const createAndApproveKyc = async (
    customerId: string,
    reviewNotes?: string,
    adminOverride: boolean = true
): Promise<KycRequest> => {
    const response = await httpClient.post<KycRequest>(API_ENDPOINTS.CRM.KYC_CREATE_AND_APPROVE, {
        customer_id: customerId,
        review_notes: reviewNotes,
        admin_override: adminOverride,
    });
    return response.data;
};

export const uploadKycDocuments = async (
    kycRequestId: string,
    formData: FormData
): Promise<{ success: boolean; message: string; documents: any[] }> => {
    const response = await httpClient.post(
        `${API_ENDPOINTS.CRM.KYC}/${kycRequestId}/upload-documents`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return response.data;
};

export const viewKycDocument = async (kycRequestId: string, documentId: string) => {
    try {
        const response = await httpClient.get(
            API_ENDPOINTS.CRM.KYC_DOCUMENTS.VIEW(kycRequestId, documentId)
        );

        // Check if response contains a presigned URL (S3) or is a direct response (local)
        if (response.data.success && response.data.data?.url) {
            // S3 presigned URL - open directly
            window.open(response.data.data.url, '_blank');
        } else {
            // Local storage - use the original endpoint
            const viewUrl = API_ENDPOINTS.CRM.KYC_DOCUMENTS.VIEW(kycRequestId, documentId);
            window.open(viewUrl, '_blank');
        }
    } catch (error) {
        console.error('Error viewing KYC document:', error);
        throw error;
    }
};

export const downloadKycDocument = async (kycRequestId: string, documentId: string) => {
    try {
        const response = await httpClient.get(
            API_ENDPOINTS.CRM.KYC_DOCUMENTS.DOWNLOAD(kycRequestId, documentId)
        );

        // Check if response contains a presigned URL (S3) or is a direct response (local)
        if (response.data.success && response.data.data?.url) {
            // S3 presigned URL - create download link directly
            const link = document.createElement('a');
            link.href = response.data.data.url;
            link.setAttribute('download', response.data.data.filename || 'document');
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Local storage - handle as blob
            const blobResponse = await httpClient.get(
                API_ENDPOINTS.CRM.KYC_DOCUMENTS.DOWNLOAD(kycRequestId, documentId),
                {
                    responseType: 'blob',
                }
            );

            // Create a blob URL from the response data
            const blob = new Blob([blobResponse.data]);
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element to trigger the download
            const link = document.createElement('a');
            link.href = url;

            // Try to get the filename from the Content-Disposition header
            const contentDisposition = blobResponse.headers['content-disposition'];
            let filename = 'document';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }

        return true;
    } catch (error) {
        console.error('Error downloading KYC document:', error);
        throw error;
    }
};
